import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { otherProductAdminAPI } from '../../services/api';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton, filterSelectClass } from '../../components/SuperAdmin/ListTools';
import {
  ContentPageHeader, ContentStats, ContentListCard, ContentEmpty, ContentThumb, ContentRow,
  ReorderButtons, LiveToggle, RowActions, ContentModal, Field, inputClass,
} from '../../components/SuperAdmin/ContentAdmin';
import { inDateRange, matchesSearch, formatExportDate } from '../../utils/exportData';
import toast from 'react-hot-toast';
import { HiOutlineCube, HiOutlineExternalLink } from 'react-icons/hi';

const Spinner = () => <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />;

const emptyForm = { name: '', url: '', description: '' };

const EMPTY_PRODUCT_FILTERS = { search: '', status: '', from: '', to: '' };

const PRODUCT_EXPORT_COLUMNS = [
  { header: 'Name', value: (p) => p.name },
  { header: 'Link', value: (p) => p.url },
  { header: 'Description', value: (p) => p.description },
  { header: 'Status', value: (p) => (p.isActive ? 'Live' : 'Hidden') },
  { header: 'Order', value: (p) => p.order },
  { header: 'Created', value: (p) => formatExportDate(p.createdAt) },
];

const ProductModal = ({ editing, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(
    editing
      ? { name: editing.name, url: editing.url, description: editing.description || '' }
      : { ...emptyForm }
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin-other-products'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const data = { name: form.name.trim(), url: form.url.trim(), description: form.description.trim() };
      return editing ? otherProductAdminAPI.update(editing._id, data) : otherProductAdminAPI.create(data);
    },
    onSuccess: () => {
      invalidate();
      toast.success(editing ? 'Product updated' : 'Product added');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save product'),
  });

  return (
    <ContentModal
      title={editing ? 'Edit product' : 'New product'}
      subtitle="Listed in the website footer under Other Products"
      onClose={onClose}
      onSave={() => saveMutation.mutate()}
      saving={saveMutation.isPending}
      canSave={!!(form.name.trim() && form.url.trim())}
    >
      <Field label="Product name" required>
        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Valley CRM" className={inputClass} />
      </Field>
      <Field label="Link" required hint="Opens in a new tab when a visitor clicks the product">
        <input type="text" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
          placeholder="https://example.com" className={`${inputClass} font-mono`} />
      </Field>
      <Field label="Description" hint="Optional — one short line shown in the footer">
        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className={inputClass} />
      </Field>
    </ContentModal>
  );
};

const OtherProducts = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-other-products'],
    queryFn: async () => (await otherProductAdminAPI.getAll()).data,
  });
  const products = data?.data || [];
  const [filters, setFilters] = useState(EMPTY_PRODUCT_FILTERS);
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const filtersActive = Object.keys(EMPTY_PRODUCT_FILTERS).some((k) => filters[k]);
  const visibleProducts = products.filter((p) =>
    matchesSearch(filters.search, p.name, p.url, p.description)
    && (!filters.status || (filters.status === 'live' ? p.isActive : !p.isActive))
    && inDateRange(p.createdAt, filters.from, filters.to));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin-other-products'] });

  const toggleMutation = useMutation({ mutationFn: (id) => otherProductAdminAPI.toggle(id), onSuccess: invalidate });
  const deleteMutation = useMutation({
    mutationFn: (id) => otherProductAdminAPI.delete(id),
    onSuccess: () => { invalidate(); toast.success('Product deleted'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete product'),
  });
  const reorderMutation = useMutation({
    mutationFn: (order) => otherProductAdminAPI.reorder(order),
    onSuccess: invalidate,
  });

  const move = (index, dir) => {
    const next = [...products];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorderMutation.mutate(next.map((p) => p._id));
  };

  const openNew = () => { setEditing(null); setShowModal(true); };
  const openEdit = (p) => { setEditing(p); setShowModal(true); };

  return (
    <div className="p-6 space-y-6">
      <ContentPageHeader
        icon={HiOutlineCube}
        title="Other Products"
        description="Links to our other products, listed in the website footer under Other Products."
        actionLabel="New Product"
        onAction={openNew}
      />

      {products.length > 0 && <ContentStats items={products} noun="products" />}

      <ContentListCard
        filters={products.length > 0 && (
          <>
            <input
              type="text"
              placeholder="Search name, link, description…"
              value={filters.search}
              onChange={(e) => setFilter({ search: e.target.value })}
              className={`${filterSelectClass} flex-1 sm:min-w-[240px]`}
            />
            <FilterSelect value={filters.status} onChange={(v) => setFilter({ status: v })}
              allLabel="All Status" options={[['live', 'Live'], ['hidden', 'Hidden']]} />
            <DateRangeFilter label="Created" from={filters.from} to={filters.to}
              onChange={({ from, to }) => setFilter({ from, to })} />
            <ResetFiltersButton active={filtersActive} onReset={() => setFilters(EMPTY_PRODUCT_FILTERS)} />
            <ExportMenu fileName="other-products" columns={PRODUCT_EXPORT_COLUMNS} getRows={() => visibleProducts} />
          </>
        )}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-14"><Spinner /></div>
        ) : products.length === 0 ? (
          <ContentEmpty icon={HiOutlineCube} title="No products yet"
            hint="Add a product to list it in the website footer." actionLabel="New Product" onAction={openNew} />
        ) : visibleProducts.length === 0 ? (
          <ContentEmpty icon={HiOutlineCube} title="No products match these filters" />
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleProducts.map((p, i) => (
              <ContentRow
                key={p._id}
                isActive={p.isActive}
                // Reordering a filtered subset would scramble the real order, so only when unfiltered
                reorder={!filtersActive && (
                  <ReorderButtons isFirst={i === 0} isLast={i === products.length - 1}
                    disabled={reorderMutation.isPending} onUp={() => move(i, -1)} onDown={() => move(i, 1)} />
                )}
                thumb={<ContentThumb icon={HiOutlineCube} />}
                title={p.name}
                meta={(
                  <a href={p.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 min-w-0 text-primary-600 hover:underline">
                    <span className="truncate">{p.url}</span>
                    <HiOutlineExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                )}
                description={p.description}
                controls={(
                  <>
                    <LiveToggle isActive={p.isActive} disabled={toggleMutation.isPending} onToggle={() => toggleMutation.mutate(p._id)} />
                    <RowActions name={p.name} onEdit={() => openEdit(p)} onDelete={() => deleteMutation.mutate(p._id)} />
                  </>
                )}
              />
            ))}
          </div>
        )}
      </ContentListCard>

      {showModal && <ProductModal editing={editing} onClose={() => setShowModal(false)} />}
    </div>
  );
};

export default OtherProducts;
